import React from 'react';
import { withNamespaces } from 'react-i18next';
import { Typography, Button, DialogContent, Dialog, DialogTitle, DialogActions, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/styles';
import SignaturePad from 'signature_pad';

const styles = {
  dialogPaper: {
    width: '560px',
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: '12px',
  },
  dialogTitle: {
    padding: '16px 20px',
    borderBottom: '1px solid #E0E0E0',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleText: {
    color: '#212121',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  closeButton: {
    color: '#616161',
    padding: 8,
  },
  dialogContent: {
    padding: '20px',
  },
  helperText: {
    color: '#ff5100',
    marginBottom: '16px',
    lineHeight: '20px',
  },
  canvasFrame: {
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    backgroundColor: '#FAFAFA',
    padding: '8px',
  },
  canvas: {
    display: 'block',
    border: '1px solid #EEEEEE',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    width: '100%',
    height: '220px',
  },
  dialogActions: {
    justifyContent: 'space-between',
    padding: '0 20px 20px',
  },
  actionButton: {
    width: 'calc(50% - 8px)',
    height: '40px',
  },
};

@withStyles(styles)
@withNamespaces('modules', { wait: true })
export default class SignaturePadContainer extends React.Component {
  state = {
    open: this.props.open,
  };

  constructor(props) {
    super(props);
    this._isWindows = navigator.appVersion.indexOf('Win') !== -1;
    this._global = window;
    this._useSigWeb = false;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      this.teardownPad();
    }
  }

  componentWillUnmount() {
    this.teardownPad();
  }

  canUseSigWeb = () => {
    const sigWebFns = ['SetDisplayXSize', 'SetDisplayYSize', 'SetTabletState', 'SetJustifyMode', 'ClearTablet'];
    return this._isWindows && sigWebFns.every((fnName) => typeof this._global[fnName] === 'function');
  }

  teardownPad = () => {
    if (this.tmr && typeof this._global.SetTabletState === 'function') {
      this._global.SetTabletState(0, this.tmr);
      this.tmr = null;
    }

    if (this.signaturePad) {
      this.signaturePad.off();
    }
  }

  isCanvasEmpty = (canvas) => {
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const { width, height } = canvas;
    const image = ctx.getImageData(0, 0, width, height).data;
    for (let i = 3; i < image.length; i += 4) {
      if (image[i] !== 0) return false;
    }
    return true;
  }

  startSign = () => {
    const providerCnv = document.getElementById('providerCnv');
    if (!providerCnv) return;
    this._useSigWeb = false;

    if (this.signaturePad) {
      this.signaturePad.off();
    }

    this.signaturePad = new SignaturePad(providerCnv, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: 'rgb(0,0,0)',
    });

    // Change canvas aspect ratio so the actual drawing and pointer will not misalign
    providerCnv.width = providerCnv.offsetWidth * 1;
    providerCnv.height = providerCnv.offsetHeight * 1;
    providerCnv.getContext('2d').scale(1, 1);

    // Returns signature image as an array of point groups
    //const data = this.signaturePad.toData();
    // const data = this.signaturePad.toDataURL("image/jpeg");
    // const data2 = this.signaturePad.toDataURL("image/svg+xml");
    // Draws signature image from an array of point groups
    //signaturePad.fromData(data);

    // Clears the canvas
    this.signaturePad.clear();

    // Returns true if canvas is empty, otherwise returns false
    //signaturePad.isEmpty();
    // Unbinds all event handlers
    //signaturePad.off();
    // Rebinds all event handlers
    this.signaturePad.on();
  }

  startProviderSign = () => {
    const canvas = document.getElementById('providerCnv');
    if (!canvas || !this.canUseSigWeb()) {
      this.startSign();
      return;
    }

    try {
      const providerCtx = canvas.getContext('2d');
      this._global.SetDisplayXSize(500);
      this._global.SetDisplayYSize(100);
      this._global.SetTabletState(0, this.tmr);
      this._global.SetJustifyMode(0);
      this._global.ClearTablet();
      this._useSigWeb = true;

      if (this.tmr == null) {
        this.tmr = this._global.SetTabletState(1, providerCtx, 50);
      }
      else {
        this._global.SetTabletState(0, this.tmr);
        this.tmr = null;
        this.tmr = this._global.SetTabletState(1, providerCtx, 50);
      }
    }
    catch (error) {
      this._useSigWeb = false;
      this.startSign();
    }
  }

  clearSign = () => {
    const canvas = document.getElementById('providerCnv');
    if (!canvas) return;

    if (this._useSigWeb && this.canUseSigWeb()) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      this._global.ClearTablet();
    }
    else if (this.signaturePad) {
      this.signaturePad.clear();
    }
  }

  saveSign = () => {
    const { target_signature_ref, target_signature_id } = this.props;
    let data = null;
    const canvas = document.getElementById('providerCnv');

    let element = target_signature_ref;
    if (!target_signature_ref && target_signature_id) {
      element = document.getElementById(target_signature_id);
    }
    if (!element) {
      this.props.close();
      return;
    }

    const isEmpty = this._useSigWeb
      ? this.isCanvasEmpty(canvas)
      : (!this.signaturePad || this.signaturePad.isEmpty());

    if (isEmpty) {
      element.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwAAACWCAYAAABkW7XSAAAEaElEQVR4Xu3UgQkAMAwCwXb/oS10i4fLBHIG77YdR4AAgYDANViBlkQkQOALGCyPQIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiDwAIGUVl1ZhH69AAAAAElFTkSuQmCC";
      if (!target_signature_ref && target_signature_id) this.props.close(target_signature_id, element.src);
      else this.props.close();
      return;
    }

    if (this._useSigWeb && this.canUseSigWeb()) {
      data = canvas.toDataURL();
      this._global.SetTabletState(0, this.tmr);
      this.tmr = null;
    }
    else if (this.signaturePad) {
      data = this.signaturePad.toDataURL();
    }
    else {
      data = '';
    }
    element.src = data;
    if (!target_signature_ref && target_signature_id) this.props.close(target_signature_id, element.src);
    else this.props.close();
  }

  onDialogUpdate = () => {
    if (this.props.open) {
      if (this.canUseSigWeb()) {
        this.startProviderSign();
      }
      else {
        this.startSign();
      }
    }
  }

  render() {
    const { t, classes } = this.props;

    return (
      <Dialog
        open={this.props.open}
        onClose={this.props.close}
        onEntered={this.onDialogUpdate}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          <div className={classes.titleRow}>
            <Typography className={classes.titleText}>
              {t('common:signaturePad.enterSignature')}
            </Typography>
            <IconButton aria-label={t('common:buttonLabels.close')} className={classes.closeButton} onClick={this.props.close}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant='body2' className={classes.helperText}>
            {t('common:signaturePad.drawYourSignatureBelow')}
          </Typography>
          <div className={classes.canvasFrame}>
            <canvas id="providerCnv" name="providerCnv" className={classes.canvas} />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            className={`milo-btn-white ${classes.actionButton}`}
            variant='contained'
            onClick={this.clearSign}
          >
            {t('common:signaturePad.clear')}
          </Button>
          <Button
            className={`milo-btn-orange ${classes.actionButton}`}
            variant='contained'
            onClick={this.saveSign}
          >
            {t('common:signaturePad.saveAndUse')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
